import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ROUTE_ARTICLES } from "../../constants/routes";
import { useAppSelector } from "../../redux/hooks";
import { useGetAccountTagsQuery } from "../../redux/services/accountTags";
import { useGetAllArticlesQuery } from "../../redux/services/article";
import { RootState } from "../../redux/store";

import PageHeading from "../../components/common/PageHeading";
import SpringContainer from "../../components/containers/SpringContainer";
import GetStartedTemplateBanner from "../../components/features/GetStartedTemplateBanner";
import TemplateArticleFilter from "../../components/features/TemplateArticleFilter";
import Input from "../../components/form/Input";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import ArticleTableContainer from "./components/ArticleTableContainer";

export const limit = 20;

const Articles = () => {
  const navigate = useNavigate();
  const templateName = useLocation()?.state?.templateName;

  // Search state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Filter state
  const [showAutoEnabled, setShowAutoEnabled] = useState<null | boolean>(null);
  const [accountTagIds, setAccountTagIds] = useState<string[]>([]);

  // Pagination state
  const [offset, setOffset] = useState(0);

  const selectedAccount = useAppSelector(
    (state: RootState) => state.user.selectedAccount
  );
  const accountId = selectedAccount?.accountId ?? null;

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    debouncedSearch ||
      (showAutoEnabled !== null && showAutoEnabled !== undefined) ||
      accountTagIds.length > 0
  );

  // API queries
  const {
    data: articles,
    isLoading,
    refetch,
    isFetching,
    isUninitialized,
  } = useGetAllArticlesQuery(
    {
      accountId,
      offset,
      limit,
      search: debouncedSearch || "",
      showAutoEnabled,
      accountTagIds,
      includeTotalCount: true,
    },
    { skip: !accountId }
  );

  const { data: accountTagData } = useGetAccountTagsQuery(
    {
      accountId,
      offset: 0,
      limit: 100,
    },
    { skip: !accountId }
  );

  // Reset offset and refetch when filters change
  useEffect(() => {
    if (accountId) {
      setOffset(0);
      refetch();
    }
  }, [accountId, debouncedSearch, showAutoEnabled, accountTagIds, refetch]);

  // Handle pagination changes (when offset changes but filters don't)
  useEffect(() => {
    if (accountId && offset > 0) {
      refetch();
    }
  }, [accountId, offset, refetch]);

  // Handle template name from navigation state
  useEffect(() => {
    if (templateName) {
      setSearch(templateName);
      setDebouncedSearch(templateName);
    }
  }, [templateName]);

  // Handle debounced search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    // Reset template name state if clearing search
    if (templateName && !value) {
      navigate(ROUTE_ARTICLES, { state: { templateName: "" } });
    }
  };

  const handleSearchSubmit = () => {
    setDebouncedSearch(search);
  };

  // Derived values
  const articlesExists = (articles?.data ?? []).length > 0;
  const totalCount = articles?.totalCount ?? 0;
  const accountTags = accountTagData?.data ?? [];

  // Loading state
  if (isUninitialized || isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingSpinner size="32px" />
      </div>
    );
  }

  return (
    <SpringContainer>
      <div className="space-y-4">
        {/* Header with search and filters */}
        <div className="flex gap-2 items-center">
          <PageHeading
            heading="Articles"
            tooltip="Refresh"
            loading={isFetching}
            refetch={refetch}
          />
          <Input
            search
            value={search}
            placeholder="Search by title or template"
            onChange={handleSearchChange}
            onBlur={handleSearchSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Tab") {
                handleSearchSubmit();
              }
            }}
            loading={isFetching}
            width={350}
          />
          <TemplateArticleFilter
            accountTags={accountTags}
            setShowAutoEnabled={setShowAutoEnabled}
            accountTagIds={accountTagIds}
            setAccountTagIds={setAccountTagIds}
          />
        </div>

        {/* Content based on state */}
        {!articlesExists && !hasActiveFilters && <GetStartedTemplateBanner />}

        {!articlesExists && hasActiveFilters && (
          <div className="text-sm text-gray-700">
            No articles found matching the current filters
          </div>
        )}

        {articlesExists && (
          <ArticleTableContainer
            articles={articles?.data ?? []}
            showPagination={totalCount > limit}
            totalArticles={totalCount}
            offset={offset}
            setOffset={setOffset}
          />
        )}
      </div>
    </SpringContainer>
  );
};

export default Articles;
